<template>
  <!-- Impersonate Modal -->
  <kt-modal
    :visible="showImpersonateModal"
    title="Impersonate Technician"
    :dismissable="true"
    @dismiss="showImpersonateModal = false"
  >
    <template #body>
      <div class="container-fluid">
        <div v-if="isLoadingTechs" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="text-muted mt-2">Loading technicians...</p>
        </div>
        <div v-else-if="techList.length === 0" class="text-center py-4 text-muted">
          <i class="fas fa-users-slash fa-2x mb-2"></i>
          <p>No active technicians found.</p>
        </div>
        <div v-else class="list-group">
          <button
            v-for="tech in techList"
            :key="tech.Id"
            class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
            @click="switchImpersonation(tech.Id, tech.TechnicianName || tech.Name)"
          >
            <span>
              <i class="fas fa-user me-2 text-primary"></i>
              {{ tech.TechnicianName || tech.Name || `Technician #${tech.Id}` }}
            </span>
            <span v-if="tech.Id === currentTechId" class="badge bg-success">Current</span>
          </button>
        </div>
      </div>
    </template>
    <template #footer>
      <button class="btn btn-outline-secondary" @click="showImpersonateModal = false">
        <i class="fas fa-times me-1"></i>Cancel
      </button>
    </template>
  </kt-modal>

  <div class="dropdown user-dropdown">
    
    <button 
      class="btn btn-sm btn-outline-primary border-white text-white dropdown-toggle d-flex align-items-center" 
      type="button" 
      id="userDropdownMenu"
      @click="toggleDropdown"
    >
      <i class="fas fa-user-circle me-2"></i>
      <span class="d-none d-md-inline">{{ userName || 'User' }}</span>
    </button>

    <!-- Mobile backdrop -->
    <div 
      v-if="isOpen" 
      class="offcanvas-backdrop d-md-none" 
      @click="closeDropdown"
    ></div>

    <ul 
      class="dropdown-menu dropdown-menu-end" 
      :class="{ 'show': isOpen }"
      @click.stop
    >
      <li><h6 class="dropdown-header">{{ userName }}</h6></li>
      <li v-if="userEmail" class="px-3 text-muted small">{{ userEmail }}</li>
      
      <!-- Clock In/Out - mobile only -->
      <li class="d-md-none">
        <hr class="dropdown-divider">
      </li>
      <li class="d-md-none">
        <button class="dropdown-item" @click="openClockModal">
          <i class="fas fa-clock me-2"></i>
          {{ clockStatusText }}
        </button>
      </li>
      <li v-if="userRoleNames.length > 0" class="px-3 pt-2">
        <div class="d-flex flex-wrap gap-1">
          <span 
            v-for="(role, index) in userRoleNames" 
            :key="index" 
            class="badge bg-secondary-subtle text-secondary-emphasis"
          >
            {{ role }}
          </span>
        </div>
      </li>
      <li><hr class="dropdown-divider"></li>
      
      <!-- System Admin Link -->
      <li v-if="isSystemAdmin">
        <router-link class="dropdown-item fw-bold text-primary" to="/admin/migrations" @click="closeDropdown">
          <i class="fas fa-shield-halved me-2"></i>
          System Admin
        </router-link>
      </li>
      
      <!-- Impersonation Status -->
      <li v-if="isImpersonating">
        <button class="dropdown-item text-warning" @click="exitImpersonation">
          <i class="fas fa-user-secret me-2"></i>
          Exit Impersonation
        </button>
      </li>
      
      <!-- Impersonate Button (for admins or when original user is admin) -->
      <li v-if="canImpersonate">
        <button class="dropdown-item text-info" @click="openImpersonateModal">
          <i class="fas fa-user-secret me-2"></i>
          Impersonate
        </button>
      </li>
      
      <li v-if="isSystemAdmin || isImpersonating || canImpersonate"><hr class="dropdown-divider"></li>
      
      <!-- User Profile/Settings -->
      <li>
        <router-link class="dropdown-item" :to="`/tech/edit/${userId}`" @click="closeDropdown">
          <i class="fas fa-user-edit me-2"></i>
          Edit Profile
        </router-link>
      </li>
      
      <li><hr class="dropdown-divider"></li>
      <li><h6 class="dropdown-header text-muted small">My Items</h6></li>
      
      <!-- My Requests -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/job/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-list me-2"></i>
          My Requests
        </router-link>
      </li>
      
      <!-- My Assignments -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/assignments/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-clipboard-check me-2"></i>
          My Assignments
        </router-link>
      </li>
      
      <!-- My Work -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/work/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-clock me-2"></i>
          My Work
        </router-link>
      </li>
      
      <!-- My Parts -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/part/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-puzzle-piece me-2"></i>
          My Parts
        </router-link>
      </li>
      
      <!-- My Expenses -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/expense/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-hand-holding-usd me-2"></i>
          My Expenses
        </router-link>
      </li>
      
      <!-- My Mileage -->
      <li>
        <router-link 
          class="dropdown-item" 
          :to="`/mileage/list?technician=${userId}`" 
          @click="closeDropdown"
        >
          <i class="fas fa-car me-2"></i>
          My Mileage
        </router-link>
      </li>
      
      <!-- My Time Tracker -->
      <li>
        <router-link 
          class="dropdown-item" 
          to="/clock/list" 
          @click="closeDropdown"
        >
          <i class="fas fa-clock me-2"></i>
          My Time Tracker
        </router-link>
      </li>
      
      <li><hr class="dropdown-divider"></li>
      
      <!-- Notifications -->
      <li>
        <router-link 
          class="dropdown-item d-flex align-items-center" 
          to="/notification/list" 
          @click="closeDropdown"
        >
          <i class="fas fa-bell me-2"></i>
          Notifications
          <span v-if="unreadCount > 0" class="badge bg-danger ms-auto">{{ unreadCount }}</span>
        </router-link>
      </li>
      
      <li><hr class="dropdown-divider"></li>
      
      <!-- Logout -->
      <li>
        <router-link class="dropdown-item text-danger" to="/logout" @click="closeDropdown">
          <i class="fas fa-right-from-bracket me-2"></i>
          Logout
        </router-link>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useAppStore } from '@/stores/AppStore';
import { useRouter } from 'vue-router';
import { storeToRefs } from 'pinia';
import { useTechStore } from '@/stores/TechStore';
import TokenHelper from '@/services/TokenHelper';
import Cache from '@/services/Cache';
import Api from '@/services/Api';
import ApiService from '@/services/ApiService';
import Emitter from '@/services/Emitter';
import { useTimeClock } from '@/composables/useTimeClock';
import { usePermissions } from '@/composables/usePermissions';
import KtModal from '@/components/UI/KtModal.vue';

const emit = defineEmits(['open-clock-modal']);

const appStore = useAppStore();
const techStore = useTechStore();
const { isAdmin } = usePermissions();
const router = useRouter();
const isOpen = ref(false);
const isSystemAdminValue = ref(false);

// Clock status for mobile menu item
const { isClockedIn, statusText: clockStatusText } = useTimeClock();
const isImpersonatingValue = ref(false);
const impersonationLogId = ref<string | null>(null);
const unreadCount = ref(0);

// Impersonate modal state
const showImpersonateModal = ref(false);
const isLoadingTechs = ref(false);
const techList = ref<Array<any>>([]);
const originalUserCache = ref<any>(null);

const loadUnreadCount = async () => {
  try {
    unreadCount.value = await ApiService.getUnreadNotificationCount(true);
  } catch (error) {
    console.error('Error loading unread count:', error);
  }
};

// Get user info from store
// Use flat properties that work with both login and KeepAlive responses
// Fall back to nested User object for backwards compatibility with login response
const userName = computed(() => 
  appStore.account.user?.Name || 
  (appStore.account.user as any)?.User?.TechnicianName || 
  'User'
);
const userEmail = computed(() => 
  appStore.account.user?.Email || 
  (appStore.account.user as any)?.User?.Email || 
  ''
);
const userId = computed(() => 
  appStore.account.user?.UserId || 
  (appStore.account.user as any)?.User?.Id || 
  ''
);

// Get user roles from store
const userRoleNames = computed(() => {
  const roles = appStore.account.user?.Roles;
  if (!roles) return [];
  return Object.values(roles).map(role => role.Name).filter(Boolean);
});

// Get admin status from token
const isSystemAdmin = computed(() => isSystemAdminValue.value);
const isImpersonating = computed(() => isImpersonatingValue.value);

// Check if user can impersonate (current admin OR original user was admin)
const canImpersonate = computed(() => {
  // If current user is admin, they can impersonate
  if (isAdmin.value) return true;
  // If we're impersonating, check if the original user was an admin
  if (originalUserCache.value) {
    const roles = originalUserCache.value.Roles;
    if (roles) {
      return Object.values(roles).some((role: any) => 
        role?.Name?.toLowerCase() === 'admin' || 
        role?.ClaimValue?.toLowerCase() === 'sysadmin'
      );
    }
  }
  return false;
});

// Current tech ID for highlighting in the list
const currentTechId = computed(() => {
  return appStore.account.user?.UserId || null;
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
};

const closeDropdown = () => {
  isOpen.value = false;
};

const openClockModal = () => {
  closeDropdown();
  emit('open-clock-modal');
};

// Close dropdown when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const dropdown = document.querySelector('.user-dropdown');
  if (dropdown && !dropdown.contains(event.target as Node)) {
    closeDropdown();
  }
};

onMounted(async () => {
  document.addEventListener('click', handleClickOutside);
  
  // Load admin and impersonation status from token
  isSystemAdminValue.value = await TokenHelper.isSystemAdmin();
  isImpersonatingValue.value = await TokenHelper.isImpersonating();
  impersonationLogId.value = await TokenHelper.getImpersonationLogId();
  
  // Load original user cache for impersonation permission check
  originalUserCache.value = await Cache.get('original_user');
  
  // Load unread notification count
  loadUnreadCount();
});

// Open impersonate modal and load technicians
const openImpersonateModal = async () => {
  closeDropdown();
  showImpersonateModal.value = true;
  isLoadingTechs.value = true;
  
  try {
    // Load techs from store
    await techStore.load();
    // Get active techs only
    const activeTechs = await techStore.getActive();
    techList.value = activeTechs;
  } catch (error) {
    console.error('Error loading technicians:', error);
  } finally {
    isLoadingTechs.value = false;
  }
};

// Switch impersonation to a different technician
const switchImpersonation = async (techId: number, techName: string) => {
  try {
    const tenantContext = await TokenHelper.getTenantContext();
    if (!tenantContext) {
      alert('Unable to determine tenant context.');
      return;
    }
    
    // Start impersonating the new technician (backend handles the switch)
    const response = await ApiService.startImpersonation(techId, tenantContext.tenantId);
    if (response?.Token) {
      const name = response.ImpersonatedName || techName || `Technician #${techId}`;
      await appStore.setImpersonation(response.Token, name);
      window.location.reload();
    } else {
      alert('Failed to start impersonation: ' + (response?.Message || 'Unknown error'));
    }
  } catch (error) {
    console.error('Error switching impersonation:', error);
    alert('An error occurred while switching impersonation.');
  }
};

Emitter.on("notification:refresh", loadUnreadCount);

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const exitImpersonation = async () => {
  closeDropdown();
  try {
    if (impersonationLogId.value) {
      await ApiService.endImpersonation(impersonationLogId.value);
    }
    await appStore.clearImpersonation();
    window.location.reload();
  } catch (error) {
    console.error('Error ending impersonation:', error);
    await appStore.clearImpersonation();
    window.location.reload();
  }
};
</script>

<style scoped>
.user-dropdown {
  position: relative;
}

.user-dropdown .dropdown-menu {
  min-width: 250px;
  margin-top: 0.5rem;
  right: 0;
  left: auto;
}

.user-dropdown .dropdown-item {
  padding: 0.5rem 1rem;
  transition: all 0.2s;
}

.user-dropdown .dropdown-item:hover {
  background-color: #f8f9fa;
  padding-left: 1.5rem;
}

.user-dropdown .dropdown-item i {
  width: 20px;
  text-align: center;
}

.user-dropdown .dropdown-header {
  font-weight: 600;
  color: #495057;
}

/* Mobile offcanvas behavior */
.offcanvas-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

@media (max-width: 767.98px) {
  .user-dropdown .dropdown-menu {
    position: fixed !important;
    top: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    left: auto !important;
    width: 280px;
    max-width: 80vw;
    height: 100vh;
    margin: 0 !important;
    border-radius: 0;
    border: none;
    box-shadow: -4px 0 16px rgba(0, 0, 0, 0.2);
    overflow-y: auto;
    z-index: 1050;
    padding-top: 1rem;
    padding-bottom: 2rem;
    transform: translateX(100%);
    transition: transform 0.3s ease-in-out;
    display: block !important;
  }

  .user-dropdown .dropdown-menu.show {
    transform: translateX(0);
  }
}
</style>
